/**
 * ConfidenceChip — small chip placed in the assistant footer icon row
 * (PR-AAU-C). Replaces the production `ConfidenceBadge` from
 * `components/chat/ToolCallCard.tsx` for the Ask Argus surface, because
 * the founder spec requires:
 *
 *   - Marker emoji per tier (🟢 / 🟡 / 🔴 / ⏸ for "no data")
 *   - Click to reveal `rationale` from the SSE `confidence` event
 *   - A distinct ⏸ tier while the RAG pipeline isn't wired yet
 *     (PR-AAU-A emits `rationale: 'placeholder'`)
 *
 * Pure presentational — derives state via `deriveConfidenceChip`.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { deriveConfidenceChip } from '@/lib/chat-stream-state';

interface ConfidenceChipProps {
  /** 0..1 confidence scalar from the SSE `confidence` event. */
  confidence: number | undefined;
  /** Rationale string; `'placeholder'` triggers the no-data tier. */
  rationale: string | undefined;
  /** Optional click — e.g., open the response trace drawer. */
  onClick?: () => void;
  className?: string;
}

const TIER_STYLES: Record<string, string> = {
  high: 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/30 hover:bg-green-500/20',
  medium: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/20',
  low: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30 hover:bg-red-500/20',
  none: 'bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted/60',
};

export function ConfidenceChip({
  confidence,
  rationale,
  onClick,
  className,
}: ConfidenceChipProps) {
  const chip = deriveConfidenceChip(confidence, rationale);
  const tierStyle = TIER_STYLES[chip.tier];

  const body = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors',
        tierStyle,
        className,
      )}
      data-testid="confidence-chip"
      data-tier={chip.tier}
    >
      <span>{chip.marker}</span>
      <span>{chip.label}</span>
    </button>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent side="top" className="text-xs max-w-[260px]">
        {chip.tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
